<template>
  <div class="view-ebook-viewer" :class="{ 'is-fullscreen': isFullscreen }">
    <div class="viewer-toolbar">
      <button class="btn btn-back p-0" @click="goBack">
        <i class="icon-chevron-left mr-2"></i> Voltar
      </button>
      <span v-if="currentFileMeta" class="viewer-title">
        {{ currentFileMeta.title }}
      </span>
      <button
        v-if="currentFileMeta && fullscreenSupported"
        class="btn btn-icon btn-fullscreen ml-auto"
        :aria-label="isFullscreen ? 'Sair de tela cheia' : 'Tela cheia'"
        :title="isFullscreen ? 'Sair de tela cheia (F)' : 'Tela cheia (F)'"
        @click="toggleFullscreen"
      >
        <i
          :class="isFullscreen ? 'fa-solid fa-compress' : 'fa-solid fa-expand'"
        ></i>
      </button>
    </div>

    <div class="viewer-body">
      <div v-if="metaLoading" class="viewer-page-wrapper">
        <div class="page-skeleton" aria-hidden="true" />
      </div>

      <template v-else-if="currentFileMeta">
        <div
          class="viewer-page-wrapper"
          @touchstart="onTouchStart"
          @touchend="onTouchEnd"
          @touchcancel="onTouchCancel"
        >
          <div v-if="pageError" class="viewer-error" role="alert">
            <p>{{ pageError }}</p>
            <button class="btn btn-primary btn-sm" @click="retryPage">
              Tentar novamente
            </button>
          </div>

          <div
            v-else-if="pageLoading && !pageBlobUrl"
            class="page-skeleton"
            aria-hidden="true"
          />

          <transition name="page-fade" mode="out-in">
            <img
              v-if="pageBlobUrl && !pageError"
              :key="currentPage"
              :src="pageBlobUrl"
              :alt="`Página ${currentPage} de ${totalPages}`"
              class="viewer-page-img"
            />
          </transition>
        </div>

        <div
          class="viewer-controls"
          role="group"
          aria-label="Navegação do livro"
        >
          <button
            class="btn btn-nav"
            :disabled="currentPage <= 1 || (pageLoading && !pageBlobUrl)"
            @click="prevPage"
            aria-label="Página anterior"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>

          <div class="viewer-progress" aria-live="polite">
            <input
              ref="pageInput"
              type="number"
              class="page-input"
              :value="currentPage"
              :min="1"
              :max="totalPages"
              aria-label="Ir para página"
              @change="onPageInputChange"
              @keydown.stop
            />
            <span class="page-total">de {{ totalPages }}</span>
          </div>

          <button
            class="btn btn-nav"
            :disabled="
              currentPage >= totalPages || (pageLoading && !pageBlobUrl)
            "
            @click="nextPage"
            aria-label="Próxima página"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <input
          v-if="totalPages > 1"
          type="range"
          class="page-range"
          :value="currentPage"
          :min="1"
          :max="totalPages"
          aria-label="Navegação por página"
          @change="onRangeChange"
        />
      </template>

      <div v-else class="viewer-empty">
        <p>Não foi possível carregar o ebook. Tente novamente mais tarde.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

const PAGE_CACHE_LIMIT = 6;
const LAST_PAGE_KEY_PREFIX = "ebookReader:lastPage:";
const RETRY_DELAY_429 = 1000;
const RETRY_DELAY_503 = 2000;
const RETRY_DELAY_403 = 200;
const SWIPE_MIN_DISTANCE = 50; // px
const SWIPE_MAX_DURATION = 600; // ms — beyond this it's a slow drag, not a swipe
const SWIPE_HORIZONTAL_RATIO = 1.5; // dx must dominate dy by this ratio
const MAX_RETRY_ATTEMPTS = 5;
const MAX_RETRY_DELAY = 10000;
const RETRY_JITTER_MS = 500;

function backoffDelay(baseMs, attempt) {
  const exp = Math.min(baseMs * Math.pow(2, attempt), MAX_RETRY_DELAY);
  return exp + Math.random() * RETRY_JITTER_MS;
}

function parseRetryAfter(headerValue, fallbackMs) {
  if (!headerValue) return fallbackMs;
  const seconds = parseInt(headerValue, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  return fallbackMs;
}

export default {
  name: "EbookViewer",
  data() {
    return {
      currentPage: 1,
      pageBlobUrl: null,
      pageLoading: false,
      pageError: null,
      retryTimeout: null,
      pageCache: new Map(),
      isFullscreen: false,
      // touchStart is non-reactive (no template depends on it). Kept on the
      // instance via $options.touch to avoid Vue's getter/setter overhead.
      touchStart: null
    };
  },
  computed: {
    ...mapState("ebookReader", ["currentFileMeta", "metaLoading"]),
    ...mapGetters(["homeRoute"]),
    fileId() {
      return this.$route.params.fileId;
    },
    totalPages() {
      return this.currentFileMeta ? this.currentFileMeta.page_count : 0;
    },
    lastPageStorageKey() {
      return `${LAST_PAGE_KEY_PREFIX}${this.fileId}`;
    },
    fullscreenSupported() {
      return (
        typeof document !== "undefined" &&
        (typeof document.fullscreenEnabled === "boolean"
          ? document.fullscreenEnabled
          : true)
      );
    }
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("fullscreenchange", this.onFullscreenChange);

    this.$store
      .dispatch("ebookReader/fetchFileMeta", this.fileId)
      .then(meta => {
        const startPage = this.restoreLastPage(meta && meta.page_count);
        this.fetchPage(startPage);
      })
      .catch(() => {
        // meta fetch failed — viewer-empty will render
      });
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    if (document.fullscreenElement && document.exitFullscreen) {
      // Leave fullscreen on unmount so the user doesn't get stuck in an
      // immersive view of an empty page after navigating away.
      document.exitFullscreen().catch(() => {});
    }
    this.clearCache();
  },
  methods: {
    handleKeydown(event) {
      const target = event.target;
      const tag = target && target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (target && target.isContentEditable)
      ) {
        return;
      }
      // Buttons handle Space/Enter natively as activation. Let those through
      // so a focused prev/next/fullscreen button keeps working with the
      // keyboard. Other keys (arrow, j, k, f, …) still drive the reader.
      if (tag === "BUTTON" && (event.key === " " || event.key === "Enter")) {
        return;
      }
      switch (event.key) {
        case "ArrowRight":
        case " ":
        case "PageDown":
        case "j":
          event.preventDefault();
          this.nextPage();
          break;
        case "ArrowLeft":
        case "PageUp":
        case "k":
          event.preventDefault();
          this.prevPage();
          break;
        case "Home":
          event.preventDefault();
          this.goToPage(1);
          break;
        case "End":
          event.preventDefault();
          if (this.totalPages) this.goToPage(this.totalPages);
          break;
        case "f":
        case "F":
          if (this.fullscreenSupported) {
            event.preventDefault();
            this.toggleFullscreen();
          }
          break;
      }
    },
    fetchPage(pageNumber, options = {}) {
      const { silent = false, retried403 = false, retryAttempt = 0 } = options;

      if (!silent && retryAttempt === 0 && !retried403) {
        if (this.retryTimeout) {
          clearTimeout(this.retryTimeout);
          this.retryTimeout = null;
        }
        this.pageError = null;
      }

      const cached = this.pageCache.get(pageNumber);
      if (cached) {
        this.touchCache(pageNumber);
        if (!silent) {
          this.pageBlobUrl = cached;
          this.currentPage = pageNumber;
          this.persistLastPage(pageNumber);
          this.prefetchAround(pageNumber);
        }
        return Promise.resolve(cached);
      }

      if (!silent) this.pageLoading = true;

      return this.$store
        .dispatch("ebookReader/fetchPageBlob", {
          fileId: this.fileId,
          pageNumber
        })
        .then(blobUrl => {
          this.cachePage(pageNumber, blobUrl);
          if (!silent) {
            this.pageBlobUrl = blobUrl;
            this.currentPage = pageNumber;
            this.persistLastPage(pageNumber);
            this.prefetchAround(pageNumber);
          }
          return blobUrl;
        })
        .catch(error => {
          if (silent) return null;
          const response = error && error.response;
          const status = response && response.status;
          const retryAfterHeader =
            response && response.headers && response.headers["retry-after"];

          if (status === 429 || status === 503) {
            if (retryAttempt >= MAX_RETRY_ATTEMPTS) {
              this.pageError =
                "Não foi possível carregar a página. Tente novamente.";
              return;
            }
            const baseDelay =
              status === 503 ? RETRY_DELAY_503 : RETRY_DELAY_429;
            const delay = parseRetryAfter(
              retryAfterHeader,
              backoffDelay(baseDelay, retryAttempt)
            );
            this.retryTimeout = setTimeout(() => {
              this.retryTimeout = null;
              this.fetchPage(pageNumber, {
                retryAttempt: retryAttempt + 1
              });
            }, delay);
          } else if (status === 403 && !retried403) {
            // Transient authorization race (eg. partner cache flush). Retry
            // exactly once after a short delay; further 403s fall through to
            // the user-facing error below.
            this.retryTimeout = setTimeout(() => {
              this.retryTimeout = null;
              this.fetchPage(pageNumber, { retried403: true });
            }, RETRY_DELAY_403);
          } else {
            this.pageError =
              "Não foi possível carregar a página. Tente novamente.";
          }
        })
        .finally(() => {
          if (!silent) this.pageLoading = false;
        });
    },
    prefetchAround(pageNumber) {
      // Backend already pre-renders 5 pages ahead; we only need to pull the
      // bytes into the browser for the immediate neighbours so click-prev /
      // click-next is instant. Failures are silent — the user-triggered fetch
      // will retry if needed. Forward neighbour first since reading is
      // forward-biased; backward only after the forward request completes so
      // we don't compete with a user-triggered fetch on a slow connection.
      const next = pageNumber + 1;
      const prev = pageNumber - 1;
      const fetchPrev = () => {
        if (prev >= 1 && !this.pageCache.has(prev)) {
          this.fetchPage(prev, { silent: true });
        }
      };
      if (next <= this.totalPages && next >= 1 && !this.pageCache.has(next)) {
        this.fetchPage(next, { silent: true }).then(fetchPrev);
      } else {
        fetchPrev();
      }
    },
    cachePage(pageNumber, blobUrl) {
      if (this.pageCache.has(pageNumber)) {
        this.touchCache(pageNumber);
        return;
      }
      this.pageCache.set(pageNumber, blobUrl);
      while (this.pageCache.size > PAGE_CACHE_LIMIT) {
        // Skip currentPage in eviction so the displayed blob is never freed
        // out from under us — `pageBlobUrl` would point at a revoked URL on
        // the next render, leaking the underlying blob until the page is
        // re-fetched.
        let evictKey = null;
        for (const k of this.pageCache.keys()) {
          if (k !== this.currentPage) {
            evictKey = k;
            break;
          }
        }
        if (evictKey === null) break;
        const url = this.pageCache.get(evictKey);
        this.pageCache.delete(evictKey);
        if (url) URL.revokeObjectURL(url);
      }
    },
    touchCache(pageNumber) {
      // Move to most-recently-used position so LRU eviction works correctly.
      const url = this.pageCache.get(pageNumber);
      if (url) {
        this.pageCache.delete(pageNumber);
        this.pageCache.set(pageNumber, url);
      }
    },
    clearCache() {
      this.pageCache.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      this.pageCache.clear();
      this.pageBlobUrl = null;
    },
    prevPage() {
      if (this.currentPage > 1) this.goToPage(this.currentPage - 1);
    },
    nextPage() {
      if (this.currentPage < this.totalPages)
        this.goToPage(this.currentPage + 1);
    },
    goToPage(pageNumber) {
      const target = Math.max(
        1,
        Math.min(this.totalPages || pageNumber, pageNumber)
      );
      if (target === this.currentPage && this.pageBlobUrl) return;
      this.fetchPage(target);
    },
    onPageInputChange(event) {
      const value = parseInt(event.target.value, 10);
      if (!Number.isFinite(value)) {
        event.target.value = this.currentPage;
        return;
      }
      this.goToPage(value);
    },
    onRangeChange(event) {
      const value = parseInt(event.target.value, 10);
      if (Number.isFinite(value)) this.goToPage(value);
    },
    retryPage() {
      this.fetchPage(this.currentPage);
    },
    persistLastPage(pageNumber) {
      try {
        window.localStorage.setItem(
          this.lastPageStorageKey,
          String(pageNumber)
        );
      } catch {
        // storage may be disabled (Safari private mode etc.) — ignore
      }
    },
    restoreLastPage(totalPages) {
      try {
        const raw = window.localStorage.getItem(this.lastPageStorageKey);
        const value = parseInt(raw, 10);
        if (
          Number.isFinite(value) &&
          value >= 1 &&
          (!totalPages || value <= totalPages)
        ) {
          return value;
        }
      } catch {
        // ignore
      }
      return 1;
    },
    toggleFullscreen() {
      if (!this.fullscreenSupported) return;
      if (!document.fullscreenElement) {
        const req = this.$el.requestFullscreen;
        if (req) req.call(this.$el).catch(() => {});
      } else if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    onTouchStart(event) {
      // Pinch / multi-touch: hand off to the browser so native zoom works.
      if (event.touches.length !== 1) {
        this.touchStart = null;
        return;
      }
      const t = event.touches[0];
      this.touchStart = { x: t.clientX, y: t.clientY, time: Date.now() };
    },
    onTouchEnd(event) {
      const start = this.touchStart;
      this.touchStart = null;
      if (!start) return;
      if (event.changedTouches.length !== 1) return;
      const t = event.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const dt = Date.now() - start.time;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      // Require a fast, mostly-horizontal gesture so we don't fight scrolls.
      if (
        absX < SWIPE_MIN_DISTANCE ||
        dt > SWIPE_MAX_DURATION ||
        absX < absY * SWIPE_HORIZONTAL_RATIO
      ) {
        return;
      }
      if (dx < 0) this.nextPage();
      else this.prevPage();
    },
    onTouchCancel() {
      this.touchStart = null;
    },
    goBack() {
      this.$router.push(this.homeRoute);
    }
  }
};
</script>

<style lang="scss" scoped>
.view-ebook-viewer {
  /* compensa altura do header global do Base.vue */
  padding-top: 72px;
  min-height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
  /* Prevent any descendant overflow (eg. wide rendered page, accidental
     100vw on a child) from creating a horizontal scrollbar in the
     mobile WebView. */
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;

  .viewer-toolbar {
    display: flex;
    align-items: center;
    padding: 16px 24px;
    gap: 16px;

    .btn-back {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.7);
      background: none;
      border: none;
      white-space: nowrap;

      &:hover {
        color: #fff;
        text-decoration: none;
      }
    }

    .viewer-title {
      font-size: 15px;
      font-weight: 500;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .btn-fullscreen {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.18);
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease, color 0.15s ease;
      padding: 0;

      &:hover {
        background: rgba(62, 86, 172, 0.3);
        border-color: #3e56ac;
        color: #fff;
      }
    }
  }

  &.is-fullscreen {
    background: #0a0d18;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    /* The browser hides the surrounding chrome in fullscreen, so the
       compensation for the global Base.vue header is no longer needed. */
    padding-top: 0;
    min-height: 100vh;

    /* Page wrapper expands to fill the full viewport so pinch zoom feels
       natural and the rendered WebP can grow as wide as the screen allows.
       We use percentage units (relative to the parent) to avoid the
       viewport-unit gotcha where 100vw includes scrollbar / safe-area on
       some mobile WebViews and creates horizontal scroll. */
    .viewer-page-wrapper {
      max-width: 100%;
    }

    /* Mobile fullscreen: tighten chrome and let the page take the entire
       width so the reader feels truly immersive. */
    @media (max-width: 768px) {
      .viewer-toolbar {
        padding: 8px 12px;
        gap: 8px;

        .viewer-title {
          font-size: 13px;
        }

        .btn-fullscreen {
          width: 32px;
          height: 32px;
        }
      }

      .viewer-body {
        padding: 0;
        /* Reserve space for the sticky controls bar so the rendered image
           never sits underneath it. */
        padding-bottom: 96px;
      }

      .viewer-page-wrapper {
        max-width: 100%;
      }

      .viewer-page-img {
        border-radius: 0;
      }

      /* Pin controls + range to the bottom of the viewport so they stay
         reachable on long pages and there's no dead black space when the
         rendered image is shorter than the screen. Translucent background
         keeps the page visible behind. */
      .viewer-controls,
      .page-range {
        position: fixed;
        left: 0;
        right: 0;
      }

      .viewer-controls {
        bottom: 28px;
        margin-top: 0;
        gap: 16px;
        justify-content: center;
      }

      .page-range {
        bottom: 14px;
        margin: 0 auto;
        width: calc(100% - 24px);
        max-width: 100%;
      }
    }
  }

  .viewer-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 16px 32px;
  }

  .viewer-page-wrapper {
    width: 100%;
    max-width: 700px;
    position: relative;
    min-height: 300px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  .viewer-page-img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    display: block;
  }

  .page-skeleton {
    width: 100%;
    /* aspect ratio aproximada de A4 retrato (1:1.41) — placeholder antes de
       sabermos o tamanho real da página */
    aspect-ratio: 1 / 1.41;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0.08) 50%,
      rgba(255, 255, 255, 0.04) 100%
    );
    background-size: 200% 100%;
    animation: page-skeleton-shimmer 1.4s ease-in-out infinite;
  }

  @keyframes page-skeleton-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .page-fade-enter-active,
  .page-fade-leave-active {
    transition: opacity 150ms ease;
  }
  .page-fade-enter,
  .page-fade-leave-to {
    opacity: 0;
  }

  .viewer-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 32px 16px;
    font-size: 14px;
    opacity: 0.7;
  }

  .viewer-controls {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-top: 24px;

    .btn-nav {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.05);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease, color 0.15s ease;
      padding: 0;

      i {
        /* Force the chevron color in case a global rule on .btn or <i>
           overrides the button's color. */
        color: inherit;
        font-size: 14px;
      }

      &:not(:disabled):hover {
        background: rgba(62, 86, 172, 0.3);
        border-color: #3e56ac;
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }

    .viewer-progress {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      min-width: 130px;
      justify-content: center;

      .page-input {
        width: 60px;
        padding: 4px 8px;
        text-align: center;
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 4px;
        color: inherit;
        font-size: 14px;

        &:focus {
          outline: none;
          border-color: #3e56ac;
          background: rgba(255, 255, 255, 0.1);
        }

        /* hide native spinner in webkit */
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        -moz-appearance: textfield;
      }

      .page-total {
        opacity: 0.7;
      }
    }
  }

  .page-range {
    width: 100%;
    max-width: 700px;
    margin: 16px 0 0;
    appearance: none;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.12);
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #3e56ac;
      cursor: pointer;
      border: 2px solid rgba(255, 255, 255, 0.85);
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #3e56ac;
      cursor: pointer;
      border: 2px solid rgba(255, 255, 255, 0.85);
    }
  }

  .viewer-empty {
    padding: 64px 16px;
    text-align: center;
    font-size: 14px;
    opacity: 0.7;
  }
}
</style>
